import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import UserSettingsRepository from '@/repositories/UserSettingsRepository';
import {
  clearSettings,
  getAllSettings,
  hasPending,
  hydrateFromServer,
  restorePending,
  setChangeHandler,
  takePending
} from '@/services/userSettingsStore';
import { FrontendSettings } from '@/types/userSettings';

export const USER_SETTINGS_QUERY_KEY = 'userSettings';

/** Batches the rapid changes a control like a theme toggle produces. */
const FLUSH_DELAY_MS = 500;

/** How long requeued changes wait after a failed push before trying again. */
const RETRY_DELAY_MS = 5000;

/**
 * Wires the settings store to the server: pulls the stored bucket once after
 * login, and pushes local changes back debounced.
 *
 * Failure is deliberately quiet. Settings are a convenience, so a failed fetch
 * leaves the app running on the localStorage cache rather than toasting an
 * error the user can do nothing about.
 */
const useUserSettingsSync = ({ enabled }: { enabled: boolean }) => {
  const repository = UserSettingsRepository();
  const queryClient = useQueryClient();
  // Rebuilt every render, so it is read through a ref rather than depended on.
  const repositoryRef = useRef(repository);
  repositoryRef.current = repository;
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hydratedRef = useRef(false);
  const inFlightRef = useRef(false);
  const wasEnabledRef = useRef(enabled);

  const flush = useRef(() => {});

  const schedule = useRef<(delay: number) => void>(() => {});
  schedule.current = (delay) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => flush.current(), delay);
  };

  const { mutate: pushSettings } = useMutation({
    mutationFn: (settings: FrontendSettings) => repository.patchFrontendSettings(settings),
    onMutate: () => {
      inFlightRef.current = true;
    },
    onError: (_error, settings) => {
      restorePending(settings);
    },
    onSettled: (_data, error) => {
      inFlightRef.current = false;

      // Nothing else drains the queue: a failed push put its changes back, and
      // a successful one may have been overtaken by an edit made while it was
      // in flight. Both cases need a flush scheduled from here.
      if (hasPending()) schedule.current(error ? RETRY_DELAY_MS : FLUSH_DELAY_MS);
    }
  });

  // Read once per page load and never again on its own. Settings only change
  // because this tab changed them, and it already knows when that happens, so
  // every automatic refetch would be a wasted request -- most visibly one per
  // navigation, since consumers mount and unmount as pages come and go.
  const { data: serverSettings } = useQuery<FrontendSettings>({
    enabled,
    queryKey: [USER_SETTINGS_QUERY_KEY],
    queryFn: () => repository.getFrontendSettings(),
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
    // Settings are a convenience: a failed read leaves the app on the
    // localStorage cache rather than toasting an error the user can't act on.
    retry: false
  });

  flush.current = () => {
    if (!enabled || !hasPending()) return;

    // One request at a time. Two pushes racing can settle out of order and
    // leave the server holding the older of two edits to the same key.
    // Whatever accumulates meanwhile goes out from onSettled.
    if (inFlightRef.current) return;

    pushSettings(takePending());
  };

  useEffect(() => {
    setChangeHandler(() => schedule.current(FLUSH_DELAY_MS));

    return () => {
      setChangeHandler(null);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  // Signing out hands the browser back. Drop this user's cached settings, the
  // changes that never reached the server, and the fetched bucket, so the next
  // person to sign in starts from their own copy instead of inheriting these.
  useEffect(() => {
    if (wasEnabledRef.current && !enabled) {
      if (timerRef.current) clearTimeout(timerRef.current);
      clearSettings();
      hydratedRef.current = false;
      queryClient.removeQueries({ queryKey: [USER_SETTINGS_QUERY_KEY] });
    }

    wasEnabledRef.current = enabled;
  }, [enabled, queryClient]);

  useEffect(() => {
    if (!serverSettings || hydratedRef.current) return;
    hydratedRef.current = true;

    const local = getAllSettings();
    hydrateFromServer(serverSettings);

    // First run against an empty bucket: adopt whatever the user already had
    // locally so nobody loses settings by being migrated.
    if (Object.keys(serverSettings).length === 0 && Object.keys(local).length > 0) {
      pushSettings(local);
    }
  }, [serverSettings, pushSettings]);

  // A pending change would otherwise be lost if the tab closed inside the
  // debounce window. Sent outside React Query because the browser cancels
  // ordinary XHR work once unload begins; this one goes out keepalive.
  useEffect(() => {
    const flushOnUnload = () => {
      if (!enabled || !hasPending()) return;
      repositoryRef.current.flushFrontendSettings(takePending());
    };

    window.addEventListener('beforeunload', flushOnUnload);
    return () => window.removeEventListener('beforeunload', flushOnUnload);
  }, [enabled]);
};

export default useUserSettingsSync;
